import {
  COMPLIANCE_CADENCES,
  COMPLIANCE_CADENCE_LABELS,
  type ComplianceCadence,
  type CreateDeviceTypeInput,
} from '@sitewise/shared';
import {
  Button,
  Input,
  Label,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  toast,
} from '@sitewise/ui';
import { useMutation } from '@tanstack/react-query';
import { useId, useState, type FormEvent } from 'react';

import { createDeviceType } from '../../../api/compliance.js';
import { ApiError } from '../../../lib/api-client.js';

interface Props {
  onClose: () => void;
  onCreated: () => void;
}

interface FormState {
  name: string;
  slug: string;
  isComplianceTracked: boolean;
  defaultCadence: ComplianceCadence | '';
  defaultCustomIntervalDays: number | '';
  defaultGracePeriodDays: number;
  requiresUnit: boolean;
  regulatoryNotes: string;
}

const EMPTY: FormState = {
  name: '',
  slug: '',
  isComplianceTracked: true,
  defaultCadence: 'annually',
  defaultCustomIntervalDays: '',
  defaultGracePeriodDays: 0,
  requiresUnit: false,
  regulatoryNotes: '',
};

function autoSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function CreateDeviceTypeSheet({ onClose, onCreated }: Props) {
  const nameId = useId();
  const slugId = useId();
  const cadenceId = useId();
  const customDaysId = useId();
  const graceId = useId();
  const notesId = useId();

  const [form, setForm] = useState<FormState>(EMPTY);
  const [slugTouched, setSlugTouched] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function setName(name: string) {
    setForm((f) => ({
      ...f,
      name,
      slug: slugTouched ? f.slug : autoSlug(name),
    }));
  }

  const submit = useMutation({
    mutationFn: async () => {
      const input: CreateDeviceTypeInput = {
        name: form.name.trim(),
        slug: form.slug.trim(),
        isComplianceTracked: form.isComplianceTracked,
        defaultCadence: form.isComplianceTracked
          ? (form.defaultCadence as ComplianceCadence)
          : null,
        defaultCustomIntervalDays:
          form.isComplianceTracked && form.defaultCadence === 'custom'
            ? Number(form.defaultCustomIntervalDays)
            : null,
        defaultGracePeriodDays: Number(form.defaultGracePeriodDays) || 0,
        requiresUnit: form.requiresUnit,
        icon: null,
        regulatoryNotes: form.regulatoryNotes.trim() || null,
        metadata: {},
      };
      return createDeviceType(input);
    },
    onSuccess: () => {
      toast.success('Type added.');
      onCreated();
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : 'Could not create type');
    },
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    submit.mutate();
  }

  return (
    <Sheet open onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="flex flex-col">
        <SheetHeader>
          <SheetTitle>New device type</SheetTitle>
          <SheetDescription>
            Cadence and grace period seed every device of this type — individual devices can
            override the schedule.
          </SheetDescription>
        </SheetHeader>

        <form className="flex flex-1 flex-col gap-3 overflow-y-auto" onSubmit={handleSubmit}>
          <div className="grid gap-1.5">
            <Label htmlFor={nameId}>Name</Label>
            <Input
              id={nameId}
              required
              maxLength={120}
              value={form.name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Backflow Preventer"
            />
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor={slugId}>Slug</Label>
            <Input
              id={slugId}
              required
              maxLength={80}
              pattern="[a-z0-9][a-z0-9-]*[a-z0-9]"
              value={form.slug}
              onChange={(e) => {
                setSlugTouched(true);
                setForm({ ...form, slug: e.target.value });
              }}
              placeholder="backflow-preventer"
            />
            <p className="text-xs text-fg-muted">
              Stable identifier — must be unique within your org.
            </p>
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.isComplianceTracked}
              onChange={(e) =>
                setForm({ ...form, isComplianceTracked: e.target.checked })
              }
            />
            Track for compliance (schedules inspections)
          </label>

          {form.isComplianceTracked ? (
            <>
              <div className="grid gap-1.5">
                <Label htmlFor={cadenceId}>Default cadence</Label>
                <select
                  id={cadenceId}
                  required
                  className="rounded border border-border bg-surface px-3 py-2 text-sm"
                  value={form.defaultCadence}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      defaultCadence: e.target.value as ComplianceCadence,
                    })
                  }
                >
                  {COMPLIANCE_CADENCES.map((c) => (
                    <option key={c} value={c}>
                      {COMPLIANCE_CADENCE_LABELS[c]}
                    </option>
                  ))}
                </select>
              </div>

              {form.defaultCadence === 'custom' ? (
                <div className="grid gap-1.5">
                  <Label htmlFor={customDaysId}>Custom interval (days)</Label>
                  <Input
                    id={customDaysId}
                    type="number"
                    min={1}
                    max={3_650}
                    required
                    value={form.defaultCustomIntervalDays}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        defaultCustomIntervalDays:
                          e.target.value === '' ? '' : Number(e.target.value),
                      })
                    }
                  />
                </div>
              ) : null}

              <div className="grid gap-1.5">
                <Label htmlFor={graceId}>Grace period (days)</Label>
                <Input
                  id={graceId}
                  type="number"
                  min={0}
                  max={365}
                  value={form.defaultGracePeriodDays}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      defaultGracePeriodDays: Number(e.target.value) || 0,
                    })
                  }
                />
              </div>
            </>
          ) : null}

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.requiresUnit}
              onChange={(e) => setForm({ ...form, requiresUnit: e.target.checked })}
            />
            Requires a unit (per-unit devices like smoke alarms)
          </label>

          <div className="grid gap-1.5">
            <Label htmlFor={notesId}>Regulatory notes</Label>
            <textarea
              id={notesId}
              rows={3}
              maxLength={2_000}
              className="rounded border border-border bg-surface px-3 py-2 text-sm"
              value={form.regulatoryNotes}
              onChange={(e) => setForm({ ...form, regulatoryNotes: e.target.value })}
            />
          </div>

          {error ? (
            <div className="rounded border border-danger-subtle bg-danger-subtle/40 px-3 py-2 text-sm text-danger">
              {error}
            </div>
          ) : null}

          <SheetFooter className="mt-auto">
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submit.isPending || !form.name.trim() || !form.slug.trim()}
            >
              {submit.isPending ? 'Saving…' : 'Create'}
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
}
